import React, { useEffect, useState, useRef, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useSelector } from 'react-redux'
import { withAuth } from '../../auth'
import { acceptTandC } from '../../actions'
import App from './'
import { useMutation } from '@apollo/react-hooks'
import { SAVE_USER } from '../../queries/user'

import Typography from '@mui/material/Typography'
import Container from '@mui/material/Container'
import Button from '@mui/material/Button'
import { styled } from '@mui/material/styles'

const policyData = {
  title: 'ARUP VDA Tool Acceptable Use and Privacy Policy',
  sections: [
    {
      heading: 'User Notice',
      content:
        "By accessing or using the cloud product ('the VDA Tool'), you acknowledge and agree to be bound by the Privacy Policy and Arup's Acceptable Use Policy. You are responsible for (and must have sufficient authority to take) all actions that are performed on or through your Arup VDA Tool access, (and associated disclosure of data) in connection with the VDA Tool. If you have been added or invited to use the VDA Tool, Arup will administer your cloud product access (and not you) and Arup controls your use of the VDA Tool, including but not limited to adding or revoking your access to the VDA Tool, enabling or disabling third-party integrations, and managing permissions. Any content that you submit or upload to the VDA Tool may be retained, accessed, used, modified, shared, or removed by Arup who administers the cloud product and our appointees as described in Arup's Acceptable Use Policy ('the Acceptable Use Policy'). You acknowledge that your account can become managed by the entity that owns or controls the email address domain with which your access was granted or registered, as described in Arup's Privacy Policy ('the Privacy Policy').",
    },
    {
      heading: 'Acceptable Use Policy',
      effective: '[insert]',
      introduction:
        'We expect that you will not misuse or abuse our products and services. This Acceptable Use Policy explains what we mean by "misuse" or "abuse", and it also helps us identify such wrongdoings, and respond accordingly. We reserve the right to remove content that is inconsistent with this policy, even if it\'s something that is not strictly forbidden by the letter of the policy. In other words, even if you do something that isn\'t listed here verbatim, but it looks or smells like something listed here, we may still remove it. Without affecting any other remedies available to us, Arup may permanently or temporarily terminate or suspend your account or access to the services without notice or liability if Arup (in its sole discretion) determines you have violated this Acceptable Use Policy. There will be no refund of licence fees or otherwise if a user\'s account is terminated due to violation of this Acceptable Use Policy.',
      definitions: {
        services:
          'All cloud services, websites or apps owned or operated by Arup, and any related websites, sub-domains and pages.',
        content: [
          'Any information, data, text, software, code, scripts, music, sound, photos, graphics, videos, messages, tags, interactive features, or other materials that you post, upload, share, submit, or otherwise provide in any manner to the services.',
          'Any other materials, content, or data you provide to Arup or use with the Services.',
        ],
      },
      subsections: [
        {
          heading: 'Wrongful Activities',
          content:
            'We will not tolerate your using the services to undertake the following wrongful activities:',
          items: [
            "Violate the privacy of others, including publishing or posting other people's private and confidential information without their express permission, or collecting or gathering other people's personal information (including account names or information) from our services.",
            'Incite actual or threatened violence against others (e.g. harassment, stalking).',
            'Conceal the origin of any content or, misrepresent yourself (including impersonating anyone else, by spoofing, phishing, manipulating headers or other identifiers, impersonating anyone else, or falsely implying any sponsorship or association with Arup or any third party).',
            'Probe or access any part of the services other than by the publicly supported interfaces (e.g. scraping).',
            'Undertake any illegal purpose, or violate any laws (including without limitation data, privacy, and export control laws).',
            'Act in any way that may impact on health or safety, including but not limited to acts of terrorism, natural disasters, or emergency response.',
          ],
        },
        {
          heading: 'Interference',
          content:
            "It's likely that we're providing the services to you and others in your organisation and different services to other clients. We therefore cannot allow for disruption of our systems or other forms of interference. In particular we will not allow it if you:",
          items: [
            'Modify, disable, or compromise the integrity or performance of the services or related systems, network or data.',
            "Compromise the integrity of our systems. Amongst other things, we won't allow probing, scanning, or testing the vulnerability of any system or network that hosts our services. This prohibition does not apply to security assessments expressly permitted by Arup.",
            'Decipher any transmissions to or from the servers running the services.',
            'Try to get unauthorised access to the services, associated systems, networks, or data or tampering with, reverse-engineering, or hacking our services, evading any security or authentication measures.',
            "Overwhelm or attempt to overpower Arup's infrastructure by putting an excessively large load on the systems that consume extraordinary resources (CPUs, memory, disk space, bandwidth, etc.), such as: exceeding the use parameters for any given service as described in its corresponding documentation; and sending more messages via robots, spiders, offline readers, or other automated systems to our servers than a human could reasonably send in the same period of time by using a normal browser.",
          ],
        },
        {
          heading: 'Inappropriate Content',
          content:
            'Please use your judgment and use your best endeavours to keep the content on our services professional and we expect that you will not post, upload, share or otherwise provide content that:',
          items: [
            "Infringes Arup's or a third party's intellectual property or other rights, including any copyright, trademark, patent, design, trade secret, moral rights, privacy rights of publicity, or any other intellectual property right or proprietary or contractual right.",
            "You don't have the right to submit.",
            'Contains viruses, bots, worms, scripting exploits, or other similar materials.',
            'Is illegal, deceptive, obscene, defamatory, libellous, threatening, harmful to minors, pornographic (including child pornography, which we will remove and report to law enforcement), indecent, harassing, hateful or otherwise violates or infringes the rights of anyone else.',
            'Encourages tortious or illegal behaviour or that is otherwise inappropriate.',
            'Is discriminatory (i.e. it attacks others based on their race, ethnicity, national origin, religion, sex, gender, sexual orientation, disability, or medical condition).',
            'Is intended to be inflammatory.',
            'Could otherwise cause damage to Arup or any third party.',
          ],
        },
        {
          heading: 'Unsuitable Communications',
          content:
            'It is not appropriate that as part of the services or related to them to:',
          items: [
            'Make disparaging remarks about us or our partners, vendors, or affiliates.',
            'Generate or disseminate advertising, chain emails, spam or other unsolicited communications.',
            'Sell the services for commercial purposes (unless expressly permitted by Arup).',
            'Promote or advertise products or services other than your own without appropriate authorisation.',
          ],
        },
      ],
    },
    {
      heading: 'Privacy Policy',
      subsections: [
        {
          number: 1,
          heading: 'Background',
          content:
            'This Privacy Policy covers the information we collect about you when you use our products or services, or otherwise interact with us, unless a different policy is displayed. In this policy we refer to our products, including cloud based software or websites as "services" in this policy. It\'s likely that the service involves the storage of data about a company or about individuals. This policy explains your choices surrounding how we use information about you, which include how you can object to certain uses of information about you and how you can access and update certain information about you. By "personal information" we mean information that can identify you as an individual, (name, email address, telephone number, bank account details, taxation details, and accounting and financial information). Arup may collect personal information directly from you when you: register to use the Service, use the Service, post to the Arup Community forum or on our blog, contact the Arup support team, and visit our Website. Where we provide the services under contract with an organisation (for example, your employer) that organisation controls the information processed by the services. If you do not agree with this policy, do not access or use our services or interact with any other aspect of our business. You may choose not to provide your personal information but it may mean that we are unable to provide you with the Service.',
        },
        {
          number: 2,
          heading:
            'Arup may receive personal information from You about others',
          content:
            "Through your use of the Service, Arup may also collect information from you about someone else. If you provide Arup with personal information about someone else, you must ensure that you are authorised to disclose that information to Arup and that, without Arup taking any further steps required by applicable data protection or privacy laws, Arup may collect, use and disclose such information for the purposes described in this Policy. This means that you must take reasonable steps to ensure the individual concerned is aware of and/or consents to the various matters detailed in this Policy, including the fact that their personal information is being collected, the purposes for which that information is being collected, the intended recipients of that information, the individual's right to obtain access to that information, Arup's identity, and how to contact Arup. Where requested to do so by Arup, you must also assist Arup with any requests by the individual to access or update the personal information you have collected from them and entered into the Service.",
        },
        {
          number: 3,
          heading:
            'Arup collects, holds, and uses your personal information for limited purposes',
          content:
            'Arup collects your personal information so that we can provide you with the Service and any related services you may request. In doing so, Arup may use the personal information we have collected from you for purposes related to the Services including to: verify your identity, administer the Service, notify you of new or changed services offered in relation to the Service, carry out marketing or training relating to the Service, assist with the resolution of technical support issues or other issues relating to the Service, comply with laws and regulations in applicable jurisdictions, and communicate with you. By using the Service, you consent to your personal information being collected, held and used in this way and for any other use you authorise. Arup will only use your personal information for the purposes described in this Policy or with your express permission. It is your responsibility to keep any passwords used for the Service safe. You should notify us as soon as possible if you become aware of any misuse of your password, and immediately change your password within the Service or via the "Forgotten Password" process.',
        },
        {
          number: 4,
          heading: 'Arup may aggregate your non-personally identifiable data',
          content:
            'By using the Service, you agree that Arup can access, aggregate and use non-personally identifiable data Arup has collected from you. This data will in no way identify you or any other individual. Arup may use this aggregated non-personally identifiable data to: assist us to better understand how our customers are using the Service, provide our clients with further information regarding the uses and benefits of the Service, and otherwise to improve the Service.',
        },
        {
          number: 5,
          heading: 'Arup takes steps to protect your personal information',
          content:
            'Arup is committed to protecting the security of your personal information and we take all reasonable precautions to protect it from unauthorised access, modification or disclosure. Your personal information is stored on secure servers that have SSL Certificates issued by leading certificate authorities, and all Data transferred between you and the Service is encrypted. However, the Internet is not in itself a secure environment and we cannot give an absolute assurance that your information will be secure at all times. Transmission of personal information over the Internet is at your own risk and you should only enter, or instruct the entering of, personal information to the Service within a secure environment. We will advise you at the first reasonable opportunity upon discovering or being advised of a security breach where your personal information is lost, stolen, accessed, used, disclosed, copied, modified, or disposed of by any unauthorised persons or in any unauthorised manner.',
        },
        {
          number: 6,
          heading:
            'Arup only discloses your Personal Information in limited circumstances',
          content:
            'Arup will only disclose the personal information you have provided to us to entities outside the Arup group of companies if it is necessary and appropriate to facilitate the purpose for which your personal information was collected pursuant to this Policy, including the provision of the Service. Arup will not otherwise disclose your personal information to a third party unless you have provided your express consent. However, you should be aware that Arup may be required to disclose your personal information without your consent in order to comply with any court orders, subpoenas, or other legal process or investigation if such disclosure is required by law. Where possible and appropriate, we will notify you if we are required by law to disclose your personal information. Arup may disclose your personal information to overseas recipients as part of its centralised Arup Group IT services. Such countries could include Singapore, the United States of America and the United Kingdom or other countries where Arup operates.',
        },
        {
          number: 7,
          heading: 'You may request access to your personal information',
          content:
            "It is your responsibility to ensure that the personal information you provide to us is accurate, complete and up-to-date. You may request access to the information we hold about you, or request that we update or correct any personal information we hold about you, by setting out your request in writing and sending it to us. Arup will process your request as soon as reasonably practicable, provided we are not otherwise prevented from doing so on legal grounds. If we are unable to meet your request, we will let you know why. For example, it may be necessary for us to deny your request if it would have an unreasonable impact on the privacy or affairs of other individuals, or if it is not reasonable and practicable for us to process your request in the manner you have requested. In some circumstances, it may be necessary for us to seek to arrange access to your personal information through a mutually agreed intermediary. We'll only keep your personal information for as long as we require it for the purposes of providing you with the Service. However, we may also be required to keep some of your personal information for specified periods of time, for example under certain laws relating to corporations, money laundering, and financial reporting legislation.",
        },
        {
          number: 8,
          heading: 'You can opt-out of any email communications',
          content:
            'Arup may send product information, Service updates and Service notifications to you via email. Our emails will contain clear and obvious instructions describing how you can choose to be removed from any mailing list not essential to the Service. Arup will remove you at your request.',
        },
      ],
    },
  ],
}

const TERMS_ACCEPTED_STORAGE_KEY = 'accepted_t_and_c'

const PREFIX = 'AcceptTerms'
const classes = {
  block: `${PREFIX}-block`,
}
const Block = styled('div')(({ theme }) => ({
  [`&.${classes.block} > *`]: {
    margin: theme.spacing(1),
  },
}))

function renderSection(section, idx) {
  const parts = []

  if (section.heading) {
    parts.push(
      <Typography
        key="h"
        variant="h6"
        gutterBottom
        style={{ marginTop: 16, fontWeight: 700 }}
      >
        {section.number
          ? `${section.number}. ${section.heading}`
          : section.heading}
      </Typography>
    )
  }
  if (section.effective) {
    parts.push(
      <Typography
        key="eff"
        variant="body2"
        gutterBottom
        style={{ fontStyle: 'italic' }}
      >
        Effective: {section.effective}
      </Typography>
    )
  }
  if (section.content) {
    parts.push(
      <Typography key="c" variant="body2" paragraph>
        {section.content}
      </Typography>
    )
  }
  if (section.introduction) {
    parts.push(
      <Typography key="intro" variant="body2" paragraph>
        {section.introduction}
      </Typography>
    )
  }
  if (section.definitions) {
    parts.push(
      <Typography key="def-s" variant="body2" paragraph>
        <strong>Services: </strong>
        {section.definitions.services}
      </Typography>
    )
    if (Array.isArray(section.definitions.content)) {
      parts.push(
        <Typography key="def-c" variant="body2" paragraph>
          <strong>Content: </strong>
          {section.definitions.content.join(' ')}
        </Typography>
      )
    }
  }
  if (Array.isArray(section.subsections)) {
    section.subsections.forEach((sub, si) => {
      parts.push(<div key={`sub-${si}`}>{renderSection(sub, si)}</div>)
    })
  }
  if (Array.isArray(section.items)) {
    parts.push(
      <ul key="items" style={{ paddingLeft: 20, marginTop: 4 }}>
        {section.items.map((item, ii) => (
          <Typography key={ii} component="li" variant="body2" paragraph>
            {item}
          </Typography>
        ))}
      </ul>
    )
  }

  return <div key={idx}>{parts}</div>
}

const AcceptTerms = props => {
  const { userInfo, auth } = props
  const dispatch = useDispatch()
  const [saveUser] = useMutation(SAVE_USER)
  const [acceptEnabled, setAcceptEnabled] = useState(false)
  const scrollRef = useRef(null)

  const accept = async () => {
    const userInput = { email: userInfo.email }
    dispatch(acceptTandC())
    localStorage.setItem(TERMS_ACCEPTED_STORAGE_KEY, 'true')
    await saveUser({ variables: { input: userInput } })
  }

  const reset = async () => {
    auth.logout(window.location.origin)
  }

  const handleScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8
    if (atBottom) setAcceptEnabled(true)
  }, [])

  return (
    <>
      <Container maxWidth="sm">
        <Block className={classes.block}>
          <Typography variant="h4" gutterBottom>
            Terms &amp; Conditions
          </Typography>
          <Typography variant="body1" gutterBottom>
            Please read the document below then click &quot;I ACCEPT&quot;
          </Typography>
          <Button onClick={accept} color="primary" disabled={!acceptEnabled}>
            I accept
          </Button>
          <Button onClick={reset} color="primary">
            I don&apos;t accept
          </Button>
        </Block>
        <Block className={classes.block}>
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            style={{
              height: '60vh',
              overflowY: 'scroll',
              border: '1px solid #ccc',
              borderRadius: 4,
              padding: '16px 20px',
              backgroundColor: '#fafafa',
            }}
          >
            <Typography variant="h5" gutterBottom style={{ fontWeight: 700 }}>
              {policyData.title}
            </Typography>
            {policyData.sections.map((section, idx) =>
              renderSection(section, idx)
            )}
          </div>
        </Block>
      </Container>
    </>
  )
}

const AcceptTermsWall = props => {
  const { userInfo, auth } = props
  const dispatch = useDispatch()
  const { acceptedTandC } = useSelector(state => state)

  useEffect(() => {
    const hasAccepted =
      localStorage.getItem(TERMS_ACCEPTED_STORAGE_KEY) === 'true'
    if (hasAccepted && !acceptedTandC) {
      dispatch(acceptTandC())
    }
  }, [acceptedTandC, dispatch])

  useEffect(() => {
    if (acceptedTandC) {
      localStorage.setItem(TERMS_ACCEPTED_STORAGE_KEY, 'true')
    }
  }, [acceptedTandC])

  return (
    <>
      {acceptedTandC ? (
        <App userInfo={userInfo} />
      ) : (
        <AcceptTerms userInfo={userInfo} auth={auth} />
      )}
    </>
  )
}

export default withAuth(AcceptTermsWall)
